import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, UserCog } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

const ROLES = ["owner", "admin", "manager", "designer", "viewer", "external"];

export default function Team() {
  const { activeWorkspaceId, user } = useAuth();
  const [members, setMembers] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", role: "designer", name: "" });
  const [busy, setBusy] = useState(false);

  const myRole = members.find((m) => m.user?.id === user?.id)?.role;
  const canEdit = myRole === "owner" || myRole === "admin";

  const load = async () => {
    if (!activeWorkspaceId) return;
    const [m, c] = await Promise.all([
      api.get(`/workspaces/${activeWorkspaceId}/members`),
      api.get(`/workspaces/${activeWorkspaceId}/custom-roles`).catch(() => ({ data: [] })),
    ]);
    setMembers(m.data);
    setCustomRoles(c.data || []);
  };
  useEffect(() => { load(); }, [activeWorkspaceId]);

  const setRole = async (m, role, custom_role_id) => {
    try {
      await api.patch(`/workspaces/${activeWorkspaceId}/members/${m.user?.id || m.user_id}/role`, {
        role,
        custom_role_id: custom_role_id ?? m.custom_role_id ?? null,
      });
      toast.success("Role updated");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/members/invite`, form);
      setOpen(false);
      setForm({ email: "", role: "designer", name: "" });
      toast.success("Member added");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to invite member"));
    } finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader
        eyebrow="People"
        title="Team & roles"
        density="dense"
        description="Internal members count toward seats. Client reviewers via review links are unlimited and free."
        testid="team-header"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-none" data-testid="team-invite-btn"><Plus className="w-4 h-4 mr-2" />Invite member</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Invite team member</DialogTitle></DialogHeader>
              <form onSubmit={invite} className="space-y-4">
                <div>
                  <Label className="label-eyebrow mb-2 inline-block">Name (optional)</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name-input" />
                </div>
                <div>
                  <Label className="label-eyebrow mb-2 inline-block">Email</Label>
                  <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email-input" />
                </div>
                <div>
                  <Label className="label-eyebrow mb-2 inline-block">Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full rounded-none" disabled={busy} data-testid="invite-submit-btn">{busy ? "Adding…" : "Add to workspace"}</Button>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="px-4 sm:px-6 md:px-10 py-8">
        <div className="border border-border">
          <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-border label-eyebrow text-[10px] bg-muted/40">
            <div className="col-span-4">Member</div>
            <div className="col-span-3">Role</div>
            <div className="col-span-3">Custom role overlay</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-1">Joined</div>
          </div>
          {members.map((m) => (
            <div key={m.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border items-center text-sm" data-testid={`team-row-${m.id}`}>
              <div className="col-span-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center font-bold">{m.user?.name?.[0]?.toUpperCase() || "?"}</div>
                <div className="min-w-0">
                  <div className="font-semibold truncate">{m.user?.name} {m.user?.id === user?.id && <span className="text-xs">(you)</span>}</div>
                  <div className="text-xs truncate">{m.user?.email}</div>
                </div>
              </div>
              <div className="col-span-3">
                {canEdit && m.user?.id !== user?.id ? (
                  <Select value={m.role} onValueChange={(v) => setRole(m, v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`team-role-${m.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <span className="chip chip-pending">{m.role}</span>
                )}
              </div>
              <div className="col-span-3">
                {canEdit && m.user?.id !== user?.id ? (
                  <Select value={m.custom_role_id || "_none"} onValueChange={(v) => setRole(m, m.role, v === "_none" ? null : v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`team-customrole-${m.id}`}><SelectValue placeholder="— (use built-in)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">— (use built-in)</SelectItem>
                      {customRoles.map((cr) => <SelectItem key={cr.id} value={cr.id}>{cr.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="text-xs">{customRoles.find((c) => c.id === m.custom_role_id)?.name || "—"}</span>
                )}
              </div>
              <div className="col-span-1"><span className={m.status === "active" ? "chip chip-approved" : "chip chip-pending"}>{m.status}</span></div>
              <div className="col-span-1 text-xs">{new Date(m.created_at).toLocaleDateString()}</div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
